import { useState } from "react";
import {
  FaCheck,
  FaEdit,
  FaSearch,
  FaTrashAlt,
  FaFilter,
  FaCheckCircle,
} from "react-icons/fa";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  InputAdornment,
  Checkbox,
  IconButton,
  Typography,
  Box,
  Grid,
  Divider,
} from "@mui/material";

// Mock data
const users = [
  { id: "1", name: "John Doe" },
  { id: "2", name: "Jane Smith" },
  { id: "3", name: "Robert Johnson" },
  { id: "4", name: "Emily Davis" },
  { id: "5", name: "Michael Wilson" },
];

const timeLogs = [
  {
    id: "1",
    userId: "1",
    date: "2025-03-10",
    projectName: "Project Alpha",
    scaffoldId: "S-001",
    totalHours: 8.5,
    workType: "Regular",
    additionalCompensation: "Dust Work Bonus",
    status: "Approved",
  },
  {
    id: "2",
    userId: "1",
    date: "2025-03-11",
    projectName: "Project Alpha",
    scaffoldId: "S-002",
    totalHours: 9.0,
    workType: "Overtime",
    additionalCompensation: null,
    status: "Pending",
  },
  {
    id: "3",
    userId: "2",
    date: "2025-03-10",
    projectName: "Project Beta",
    scaffoldId: "S-003",
    totalHours: 7.5,
    workType: "Regular",
    additionalCompensation: null,
    status: "Approved",
  },
  {
    id: "4",
    userId: "3",
    date: "2025-03-11",
    projectName: "Project Gamma",
    scaffoldId: "S-004",
    totalHours: 10.0,
    workType: "Night Shift",
    additionalCompensation: "Chemical Work Bonus",
    status: "Pending",
  },
  {
    id: "5",
    userId: "4",
    date: "2025-03-12",
    projectName: "Project Delta",
    scaffoldId: "S-005",
    totalHours: 8.0,
    workType: "Regular",
    additionalCompensation: null,
    status: "Pending",
  },
];

export default function AdminTimeLogManagement() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [logToDelete, setLogToDelete] = useState(null);

  const filteredUsers = users?.filter((user) =>
    user?.name?.toLowerCase()?.includes(searchTerm.toLowerCase())
  );

  const filteredLogs = timeLogs.filter((log) => {
    if (selectedUser && log.userId !== selectedUser) return false;
    if (statusFilter && statusFilter !== "all" && log.status !== statusFilter)
      return false;
    return true;
  });

  const handleBulkApprove = () => {
    alert(`Approved ${selectedLogs.length} time logs`);
    setSelectedLogs([]);
  };

  const toggleLogSelection = (logId) => {
    if (selectedLogs.includes(logId)) {
      setSelectedLogs(selectedLogs.filter((id) => id !== logId));
    } else {
      setSelectedLogs([...selectedLogs, logId]);
    }
  };

  const handleSelectAllLogs = (event) => {
    if (event.target.checked) {
      setSelectedLogs(filteredLogs.map((log) => log.id));
    } else {
      setSelectedLogs([]);
    }
  };

  const handleDeleteLog = () => {
    alert(`Deleted log ${logToDelete}`);
    setLogToDelete(null);
    setIsDeleteDialogOpen(false);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={3}>
        <Card elevation={2}>
          <CardHeader title="Users" titleTypographyProps={{ variant: "h6" }} />
          <Divider />
          <CardContent sx={{ pt: 2, px: 2 }}>
            <div style={{ position: "relative", width: "100%" }}>
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                
                  fontSize: "16px",
                
                  borderRadius: "4px",
                  outline: "none",
                }}
              />
              <FaSearch
                size={16}
                style={{
                  position: "absolute",
                  left: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#999",
                }}
              />
            </div>
            <Box
              sx={{ height: "calc(100vh - 300px)", overflow: "auto", mt: 2 }}
            >
              <Button
                fullWidth
                variant={selectedUser === null ? "contained" : "outlined"}
                sx={{ justifyContent: "flex-start", mb: 1 }}
                onClick={() => setSelectedUser(null)}
              >
                All Users
              </Button>
              {filteredUsers?.map((user) => (
                <Button
                  key={user.id}
                  fullWidth
                  variant={selectedUser === user.id ? "contained" : "outlined"}
                  sx={{ justifyContent: "flex-start", mb: 1 }}
                  onClick={() => setSelectedUser(user.id)}
                >
                  {user.name}
                </Button>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={9}>
        <Card elevation={2}>
          <CardHeader
            title={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="h6">Manage Employee Time Logs</Typography>
                <Box sx={{ display: "flex", gap: 2, mt: { xs: 2, sm: 0 } }}>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="status-filter-label">
                      Filter by status
                    </InputLabel>
                    <Select
                      labelId="status-filter-label"
                      value={statusFilter}
                      label="Filter by status"
                      onChange={(e) => setStatusFilter(e.target.value)}
                      startAdornment={
                        <InputAdornment position="start">
                          <FaFilter size={14} />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="Approved">Approved</MenuItem>
                      <MenuItem value="Pending">Pending</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setSelectedUser(null);
                      setStatusFilter("");
                      setSearchTerm("");
                    }}
                  >
                    Reset Filters
                  </Button>
                  {selectedLogs.length > 0 && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<FaCheckCircle size={16} />}
                      onClick={handleBulkApprove}
                    >
                      Approve Selected ({selectedLogs.length})
                    </Button>
                  )}
                </Box>
              </Box>
            }
            titleTypographyProps={{ component: "div" }}
          />
          <Divider />
          <CardContent>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedLogs.length > 0 &&
                          selectedLogs.length < filteredLogs.length
                        }
                        checked={
                          filteredLogs.length > 0 &&
                          selectedLogs.length === filteredLogs.length
                        }
                        onChange={handleSelectAllLogs}
                      />
                    </TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Scaffold ID</TableCell>
                    <TableCell>Hours</TableCell>
                    <TableCell>Work Type</TableCell>
                    <TableCell>Compensation</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">
                          No time logs found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedLogs.includes(log.id)}
                            onChange={() => toggleLogSelection(log.id)}
                          />
                        </TableCell>
                        <TableCell>{log.date}</TableCell>
                        <TableCell>{log.projectName}</TableCell>
                        <TableCell>{log.scaffoldId}</TableCell>
                        <TableCell>{log.totalHours}</TableCell>
                        <TableCell>{log.workType}</TableCell>
                        <TableCell>
                          {log.additionalCompensation || "None"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.status}
                            color={
                              log.status === "Approved" ? "primary" : "default"
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              gap: 1,
                            }}
                          >
                            {log.status === "Pending" && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  alert(`Approved log ${log.id}`);
                                }}
                              >
                                <FaCheck size={14} />
                              </IconButton>
                            )}
                            <IconButton size="small">
                              <FaEdit size={14} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setLogToDelete(log.id);
                                setIsDeleteDialogOpen(true);
                              }}
                            >
                              <FaTrashAlt size={14} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this time log? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDeleteLog} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
